import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Pencil, FileText } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useApi, PageHeader, Loading, Card, StatusBadge, Empty, KpiCard } from "../components/common";
import { ClientForm } from "./Clients";
import { QuoteDialog } from "../components/SalesDialogs";
import { Button } from "../components/ui/button";
import { fdate, fdatetime, inr } from "../lib/format";

export default function ClientDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const { data: c, loading, reload } = useApi(`/clients/${id}`);
  const [dlg, setDlg] = useState(null);
  if (loading && !c) return <Loading />;
  if (!c) return <Empty text="Client not found or not accessible" />;
  const owner = user.role === "owner";
  return (
    <div data-testid="client-detail">
      <PageHeader title={c.name} subtitle={`${c.code} · ${c.studio_name || ""} · ${c.city || ""}`} actions={<>
        <Button variant="outline" onClick={() => setDlg("edit")} data-testid="client-edit-button"><Pencil className="h-4 w-4 mr-1" />Edit</Button>
        <Button variant="outline" onClick={() => setDlg("quote")} data-testid="client-quote-button"><FileText className="h-4 w-4 mr-1" />Quotation</Button></>} />
      {owner && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4" data-testid="client-financials">
          <KpiCard label="Invoiced" value={inr(c.invoices.filter((i) => i.status !== "cancelled").reduce((s, i) => s + i.total, 0))} tone="revenue" />
          <KpiCard label="Paid" value={inr(c.invoices.reduce((s, i) => s + i.amount_paid, 0))} tone="cash" />
          <KpiCard testid="client-outstanding" label="Outstanding" value={inr(c.outstanding)} tone="pending" />
          <KpiCard label="Projects" value={c.projects.length} tone="profit" />
        </div>
      )}
      <div className="grid lg:grid-cols-3 gap-4">
        <Card title="Contact" testid="client-contact">
          <dl className="space-y-2 text-sm">{[["Phone", c.phone], ["WhatsApp", c.whatsapp], ["Email", c.email], ["Address", c.address], ["Source", c.source], ["Since", fdate(c.created_date)]].map(([k, v]) => (
            <div key={k} className="flex justify-between gap-3"><dt className="text-muted-foreground">{k}</dt><dd className="font-medium text-right break-all">{v || "—"}</dd></div>))}</dl>
          {c.notes && <p className="text-sm mt-3 p-3 bg-muted/60 rounded-lg">{c.notes}</p>}
        </Card>
        <Card title="Projects & previous work" className="lg:col-span-2" testid="client-projects">
          {c.projects.length ? c.projects.map((p) => (
            <Link key={p.id} to={`/projects/${p.id}`} className="flex items-center justify-between py-2.5 border-b last:border-0 hover:bg-violet-50/50 -mx-2 px-2 rounded">
              <div><div className="text-sm font-medium">{p.name}</div><div className="text-xs text-muted-foreground">{p.code} · {p.album_size} · {p.total_sheets} sheets · due {fdate(p.deadline)}</div></div>
              <div className="flex items-center gap-2">{owner && <span className="font-num text-sm">{inr(p.deal_value)}</span>}<StatusBadge value={p.status} /></div>
            </Link>)) : <Empty text="No projects yet" />}
        </Card>
        <Card title="Quotations" testid="client-quotations">
          {c.quotations.length ? c.quotations.map((q) => (
            <div key={q.id} className="flex justify-between py-2 border-b last:border-0 text-sm"><span className="font-mono text-xs">{q.code}</span><span className="font-num">{inr(q.total)}</span><StatusBadge value={q.status} /></div>)) : <Empty text="No quotations" />}
        </Card>
        {owner && (
          <Card title="Invoices & payments" className="lg:col-span-2" testid="client-invoices">
            {c.invoices.length ? c.invoices.map((i) => (
              <Link key={i.id} to={`/invoices/${i.id}`} className="flex items-center justify-between py-2.5 border-b last:border-0 hover:bg-violet-50/50 -mx-2 px-2 rounded text-sm">
                <div><div className="font-mono text-xs font-semibold">{i.number}</div><div className="text-xs text-muted-foreground">{fdate(i.date)} · {i.project_name}</div></div>
                <div className="text-right"><div className="font-num">{inr(i.total)} · bal {inr(i.balance)}</div><StatusBadge value={i.payment_status} /></div>
              </Link>)) : <Empty text="No invoices" />}
          </Card>
        )}
        <Card title="Contact history" className="lg:col-span-3" testid="client-history">
          {c.activities.length ? c.activities.map((a) => (
            <div key={a.id} className="py-2 border-b last:border-0 text-sm"><div>{a.note}</div><div className="text-[11px] text-muted-foreground">{a.type} · {a.by_name} · {fdatetime(a.at)}</div></div>)) : <Empty text="No contact history" />}
        </Card>
      </div>
      <ClientForm open={dlg === "edit"} initial={c} onClose={() => setDlg(null)} onSaved={reload} />
      <QuoteDialog open={dlg === "quote"} clientId={c.id} onClose={() => setDlg(null)} onSaved={reload} />
    </div>
  );
}
